import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type UsageType = "private" | "business" | "rental" | "income_tax_other" | "private_sale";
export type OcrStatus = "pending" | "running" | "success" | "failed" | "skipped";
export type OcrConfidence = "high" | "medium" | "low";

export type ReceiptListItem = {
  id: string;
  vendor: string | null;
  vendorLegalEntity: string | null;
  vendorBranch: string | null;
  receiptDate: string | null;
  captureDate: string;
  totalAmount: number | null;
  currency: string;
  needsReview: boolean;
  reviewedAt: string | null;
  usageType: UsageType;
  ocrStatus: OcrStatus;
  ocrConfidence: OcrConfidence | null;
  source: string;
};

export type Stability = "fixed" | "variable";
// Migration 0014: Zahlungsart (spiegelt PAYMENT_METHODS im Backend-Schema).
export type PaymentMethod =
  | "cash"
  | "girocard"
  | "visa"
  | "mastercard"
  | "amex"
  | "paypal"
  | "apple_pay"
  | "google_pay"
  | "bank_transfer"
  | "direct_debit"
  | "invoice_open"
  | "gift_card"
  | "other";
// S3: „gruppe“ (Gruppe der Buchungsart) ersetzt „usageType“ als Steuer-Achse.
export type AxisType = "gruppe" | "direction" | "stability" | "category" | "subcategory" | "subSubcategory";
export const DEFAULT_AXIS_ORDER: AxisType[] = [
  "gruppe",
  "direction",
  "stability",
  "category",
  "subcategory",
  "subSubcategory",
];

export type Category = {
  id: string;
  name: string;
  colorHex: string | null;
  sfSymbolOrEmoji: string | null;
  sortOrder?: number;
  archived?: boolean;
  // Migration 0010: 4-Achsen-Modell. Gilt nur auf Leaf-Top-Cats (ohne aktive Subs) — Migration 0012.
  stability?: Stability | null;
  // Migration 0011: Neutral-Flag (Saldenverschiebung — Tilgung, Umbuchung).
  // Default false. Positionen einer Neutral-Cat gehen in den dritten Σ-Bucket.
  // ⚠️ Migration 0012: Auch nur auf Leaf-Top-Cats wirksam.
  isNeutral?: boolean;
  // Migration 0012 (2026-06-05): Default-Verwendungsart der Top-Cat (vererbt sich beim
  // Beleg-Erfassen auf receipts.usageType, falls noch nicht gesetzt). NULL = kein Default.
  defaultUsageType?: UsageType | null;
  // Oberflächen-Paket: Buchungsart-Standard (auf allen Ebenen; leere Unterebene erbt von oben).
  defaultSteuerArtId?: string | null;
};

export type Subcategory = {
  id: string;
  name: string;
  parentId: string;
  sortOrder?: number;
  archived?: boolean;
  // Migration 0012: Stability + isNeutral auf Sub-Ebene (Leaf-Konstraint).
  // Nur an Subs ohne aktive SubSubs sinnvoll setzen.
  stability?: Stability | null;
  isNeutral?: boolean;
  defaultSteuerArtId?: string | null;
};

// Migration 0010: 3. Hierarchie-Ebene (optional pro Pfad).
export type SubSubcategory = {
  id: string;
  name: string;
  parentId: string; // subcategories.id
  sortOrder?: number;
  archived?: boolean;
  // Migration 0012: SubSubs sind immer Leafs — stability + isNeutral hier ohne Constraint.
  stability?: Stability | null;
  isNeutral?: boolean;
  defaultSteuerArtId?: string | null;
};

export type LineItemDetail = {
  id: string;
  position: number;
  descriptionText: string;
  category: Category | null;
  subcategory: Subcategory | null;
  // Migration 0010
  subSubcategory?: SubSubcategory | null;
  usageTypeOverride?: UsageType | null;
  priceGross: number;
  productDiscount: number;
  // Gedruckter Coupon-Anteil (z. B. Lidl Plus), sonst null → Rest-Verteilung (18.10.2026).
  bonRabattAnteil: number | null;
  quantity: number | null;
  unit: string | null;
  pricePerUnit: number | null;
  vatRate: number | null;
  vatRateOverride: number | null;
  // Migration 0013 (Modell B): Stability + isNeutral als Snapshot auf der Position.
  // Override-Flags signalisieren explizite User-Klicks; schützen vor Cat-Wechsel-/Re-Analyze-Bleed.
  stability: Stability | null;
  isNeutral: boolean;
  hasStabilityOverride: boolean;
  hasIsNeutralOverride: boolean;
  manualOverride: boolean;
  addedManually: boolean;
  // Oberflächen-Paket: Buchungsart (Art ODER Anteile), Bestätigung, eigene Wahl, Vorschlag der Kategorie mit Grund.
  steuerArtId: string | null;
  steuerArtBestaetigt: boolean;
  hasSteuerArtOverride: boolean;
  anteile: Anteil[];
  // 24.10.2026: Instandhaltung je Position (nur Prüfliste)
  istInstandhaltung: boolean;
  massnahmeArt: "erhaltung" | "herstellung" | null;
  massnahmeBereich: "heizung" | "sanitaer" | "elektro" | "fenster" | "sonstige" | null;
  massnahme: string | null;
  lohnanteil: number | null;
  // Vorschläge (nur Anzeige, blau) — gespeichert wird erst beim Übernehmen
  massnahmeVorschlag?: { art: "erhaltung" | null; bereich: "heizung" | "sanitaer" | "elektro" | "fenster" | null; lohnanteil: number | null; massnahme: string | null };
  vorschlag: { steuerArtId: string | null; quelle: { ebene: "kategorie" | "unterkategorie" | "unter-unterkategorie"; name: string } | null };
};

export type Anteil = { steuerArtId: string; anteilBp: number; reihenfolge: number; herkunft: "hand" | "regel" };
export type Buchungsart = { id: string; name: string; topf: string; gruppe: string | null };

export type Tag = { id: string; name: string; colorHex: string | null };

export type ReceiptDetail = {
  id: string;
  dtUuid?: string | null;
  vendor: string | null;
  vendorLegalEntity: string | null;
  vendorBranch: string | null;
  receiptDate: string | null;
  captureDate: string;
  totalAmount: number | null;
  totalDiscountGross: number;
  ocrFooterVat7: number | null;
  ocrFooterVat19: number | null;
  currency: string;
  source: string;
  ocrStatus: OcrStatus;
  ocrConfidence: OcrConfidence | null;
  ocrEngine: string | null;
  ocrRawText: string | null;
  needsReview: boolean;
  reviewedAt: string | null;
  ocrIssues: { code: string; message: string; severity: string }[] | null;
  usageType: UsageType;
  // Migration 0014 (Modell B + Bezahldatum, 2026-06-05 spät)
  paymentMethod: PaymentMethod | null;
  paymentDate: string | null;
  duplicateOfReceiptId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lineItems: LineItemDetail[];
  tags: Tag[];
  steuerjahr: number | null;
};

export type ReceiptsListFilters = {
  reviewed?: boolean;
  year?: number;
  month?: number;
  sort?: "capture" | "date";
};

function buildListQuery(filters: ReceiptsListFilters): string {
  const params = new URLSearchParams();
  if (filters.reviewed !== undefined) params.set("reviewed", String(filters.reviewed));
  if (filters.year !== undefined) params.set("year", String(filters.year));
  if (filters.month !== undefined) params.set("month", String(filters.month));
  if (filters.sort !== undefined) params.set("sort", filters.sort);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function useReceipts(filters: ReceiptsListFilters = {}, enabled = true) {
  return useQuery<{ receipts: ReceiptListItem[]; count: number }>({
    queryKey: ["receipts", filters],
    queryFn: () => api.get(`/api/receipts${buildListQuery(filters)}`),
    enabled,
  });
}

export function useReceipt(id: string | undefined) {
  return useQuery<ReceiptDetail>({
    queryKey: ["receipt", id],
    queryFn: () => api.get(`/api/receipts/${id}`),
    enabled: !!id,
  });
}

export function useCategories() {
  return useQuery<{ categories: Category[]; subcategories: Subcategory[]; subSubcategories: SubSubcategory[] }>({
    queryKey: ["categories"],
    queryFn: () => api.get("/api/categories"),
    staleTime: 5 * 60_000,
  });
}

// ── Tag 7: Edit-Mutations + Audit/Tags ────────────────────────────────────

export type HeaderPatch = Partial<{
  vendor: string | null;
  vendorLegalEntity: string | null;
  vendorBranch: string | null;
  receiptDate: string | null; // YYYY-MM-DD
  ocrFooterVat7: number | null;
  ocrFooterVat19: number | null;
  totalAmount: number | null;
  totalDiscountGross: number | null;
  // Migration 0014 (2026-06-05 spät): Zahlungsart + Bezahldatum
  paymentMethod: PaymentMethod | null;
  paymentDate: string | null; // YYYY-MM-DD
  markReviewed: boolean;
}>;

export type LineItemField =
  | "descriptionText"
  | "categoryId"
  | "subcategoryId"
  // Migration 0010
  | "subSubcategoryId"
  | "priceGross"
  | "productDiscount"
  | "vatRate"
  | "vatRateOverride"
  // Migration 0013 (Modell B): Pillen-Klick im Beleg-Detail.
  | "stability"
  | "isNeutral"
  // 24.10.2026: Instandhaltung je Position
  | "massnahmeArt"
  | "massnahmeBereich"
  | "massnahme"
  | "lohnanteil";

export type AuditEntry = {
  scope: "header" | "item";
  label: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  correctedAt: string;
  user: string | null;
};

export function useUpdateReceiptHeader(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: HeaderPatch) =>
      api.patch<{ ok: true; changed: string[]; reviewed: boolean; blockedReason: string | null }>(
        `/api/receipts/${id}`,
        patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["receipt", id] });
      void qc.invalidateQueries({ queryKey: ["receipts"] });
      void qc.invalidateQueries({ queryKey: ["audit", id] });
    },
  });
}

export function useUpdateLineItem(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, field, value }: { itemId: string; field: LineItemField; value: unknown }) =>
      api.patch<{ ok: true; changed: boolean }>(`/api/receipts/${receiptId}/line-items/${itemId}`, { field, value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["receipt", receiptId] });
      void qc.invalidateQueries({ queryKey: ["audit", receiptId] });
    },
  });
}

export function useAddLineItem(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Partial<{ descriptionText: string; priceGross: number; productDiscount: number; vatRate: number | null; categoryId: string | null }>) =>
      api.post<{ ok: true; id: string; position: number }>(`/api/receipts/${receiptId}/line-items`, values),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["receipt", receiptId] }),
  });
}

export function useDeleteLineItem(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.del<{ ok: true }>(`/api/receipts/${receiptId}/line-items/${itemId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["receipt", receiptId] });
      void qc.invalidateQueries({ queryKey: ["audit", receiptId] });
    },
  });
}

export function useReceiptAudit(id: string, enabled: boolean) {
  return useQuery<{ entries: AuditEntry[]; count: number }>({
    queryKey: ["audit", id],
    queryFn: () => api.get(`/api/receipts/${id}/audit`),
    enabled: enabled && !!id,
  });
}

export function useTags() {
  return useQuery<{ tags: Tag[] }>({
    queryKey: ["tags"],
    queryFn: () => api.get("/api/tags"),
    staleTime: 60_000,
  });
}

export function useAddTag(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<{ ok: true; tag: Tag }>(`/api/receipts/${receiptId}/tags`, { name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["receipt", receiptId] });
      void qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
}

export function useRemoveTag(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => api.del<{ ok: true }>(`/api/receipts/${receiptId}/tags/${tagId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["receipt", receiptId] }),
  });
}

export type ReanalyzeResult = {
  status: "success";
  needsReview: boolean;
  reviewReasons: string[];
  protectedItemCount: number;
  freshItemCount: number;
  protectedHeaderFields: string[];
  /** true = war geprüft, liegt jetzt wieder in der Inbox. */
  zurueckInInbox: boolean;
};

export function useReanalyze(receiptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ReanalyzeResult>(`/api/receipts/${receiptId}/reanalyze`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["receipt", receiptId] });
      void qc.invalidateQueries({ queryKey: ["audit", receiptId] });
      // Ein geprüfter Beleg geht nach dem Neu-Analysieren zurück in die Inbox → Listen + Auswertungen neu laden.
      void qc.invalidateQueries({ queryKey: ["receipts"] });
      for (const k of ["reports", "report-by-category", "report-tree", "report-drill"]) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}

export function useDeleteReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hard }: { id: string; hard?: boolean }) =>
      api.del<{ ok: true; deleted: string }>(`/api/receipts/${id}${hard ? "?hard=true" : ""}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["receipts"] }),
  });
}

// ── Tag 8: Reports ────────────────────────────────────────────────────────
export type CategoryBucket = { categoryId: string | null; name: string; colorHex: string | null; isNeutral?: boolean; total: number; itemCount: number };
export type VendorBucket = { vendor: string; total: number; count: number };
// Migration 0010: totals-Split (Einnahmen/Ausgaben/Netto). `total` bleibt = totals.net (Rueckwaerts-Kompat).
// Migration 0011: zusätzlich `neutral` = Σ Math.abs(eff) für Positionen einer isNeutral-Cat (Tilgung etc.).
export type ReportTotals = { expense: number; income: number; neutral: number; net: number };
export type ReportByCategory = {
  byCategory: CategoryBucket[];
  topVendors: VendorBucket[];
  total: number;
  totals?: ReportTotals;
  receiptCount: number;
  // S3: Summe je Gruppe über alle Teile, mit „davon nur Vorschlag“ (unbestätigte Buchungsarten).
  byGruppe?: { gruppe: string; total: number; davonVorschlag: number; teilAnzahl: number }[];
};

export type ReportParams = {
  from?: string;
  to?: string;
  gruppe?: string;
  nurUnbestaetigt?: boolean;
  // Migration 0010 + 0011: neutral als Direction-Bucket (Saldenverschiebung).
  direction?: "expense" | "income" | "neutral" | "all";
  stability?: "fixed" | "variable" | "none" | "all";
};

export function useReportByCategory(params: ReportParams, enabled = true) {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.gruppe && params.gruppe !== "all") qs.set("gruppe", params.gruppe);
  if (params.nurUnbestaetigt) qs.set("nurUnbestaetigt", "1");
  if (params.direction && params.direction !== "all") qs.set("direction", params.direction);
  if (params.stability && params.stability !== "all") qs.set("stability", params.stability);
  const q = qs.toString();
  return useQuery<ReportByCategory>({
    queryKey: ["report-by-category", params],
    queryFn: () => api.get(`/api/reports/by-category${q ? `?${q}` : ""}`),
    enabled,
  });
}

// ── Track G (2026-06-05): Beleg-driven Tree ────────────────────────────────────
// Aufgelöste Achsen-Werte pro line_item. Frontend aggregiert daraus die Tree-
// Struktur (Counts pro Pfad), die nur belegte Knoten anzeigt. Endpoint liefert
// alle Belege im Zeitraum — Achsen-Filter werden nicht angewandt, weil der Tree
// die komplette Beleg-Landschaft als stabile Karte zeigen soll.
export type TreeItem = {
  receiptId: string;
  lineItemId: string;
  gruppe: string; // S3: ein Eintrag je Teil — aufgeteilte Position erscheint unter jeder Gruppe mit Teilbetrag
  bestaetigt: boolean;
  anteilBp: number | null;
  direction: "expense" | "income" | "neutral";
  stability: Stability | null;
  categoryId: string | null;
  subcategoryId: string | null;
  subSubcategoryId: string | null;
  effective: number;
};

export function useReportTree(params: { from?: string; to?: string } = {}, enabled = true) {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const q = qs.toString();
  return useQuery<{ items: TreeItem[] }>({
    queryKey: ["report-tree", params],
    queryFn: () => api.get(`/api/reports/tree${q ? `?${q}` : ""}`),
    enabled,
  });
}

// ── B4: Drill-Down aus der Auswertung (Positionen einer Kategorie / Belege eines Haendlers) ──
export type DrillPosition = {
  lineItemId: string;
  receiptId: string;
  vendor: string | null;
  receiptDate: string | null;
  description: string;
  categoryName: string;
  effective: number;
  priceGross: number;
  productDiscount: number;
  quantity: number | null;
  unit: string | null;
  anteilBp: number | null; // S3: gesetzt = Zeile ist ein Teil einer aufgeteilten Position
  gruppe: string;
};
export type ReportDrill =
  | { kind: "positions"; label: string; total: number; positions: DrillPosition[] }
  | { kind: "receipts"; label: string; total: number; receipts: ReceiptListItem[] };

export type DrillParams = {
  from?: string;
  to?: string;
  gruppe?: string;
  nurUnbestaetigt?: boolean;
  // Migration 0010/0011: direction inkl. "neutral" (Tilgung/Saldenverschiebung),
  // damit Tree-Klick auf den Neutral-Bucket korrekt durchgereicht wird.
  direction?: "expense" | "income" | "neutral" | "all";
  stability?: "fixed" | "variable" | "none" | "all";
  // categoryId/subcategoryId/subSubcategoryId: alle drei werden vom Tree gesetzt,
  // je nachdem welcher Knoten geklickt wurde. Drill-Backend filtert leaf-aware.
  categoryId?: string; // <id> | "none"
  subcategoryId?: string;
  subSubcategoryId?: string;
  vendor?: string;
};

export function useReportDrill(params: DrillParams, enabled = true) {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.gruppe && params.gruppe !== "all") qs.set("gruppe", params.gruppe);
  if (params.nurUnbestaetigt) qs.set("nurUnbestaetigt", "1");
  if (params.direction && params.direction !== "all") qs.set("direction", params.direction);
  if (params.stability && params.stability !== "all") qs.set("stability", params.stability);
  if (params.categoryId !== undefined) qs.set("categoryId", params.categoryId);
  if (params.subcategoryId !== undefined) qs.set("subcategoryId", params.subcategoryId);
  if (params.subSubcategoryId !== undefined) qs.set("subSubcategoryId", params.subSubcategoryId);
  if (params.vendor !== undefined) qs.set("vendor", params.vendor);
  const q = qs.toString();
  return useQuery<ReportDrill>({
    queryKey: ["report-drill", params],
    queryFn: () => api.get(`/api/reports/drill${q ? `?${q}` : ""}`),
    enabled,
  });
}

// ── F2: Kategorie/Subkategorie-Verwaltung (Settings) ──────────────────────
// Alle invalidieren ["categories"] (dieselbe Query, die Detail-Dropdown + Settings nutzen).
export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; colorHex?: string | null; sfSymbolOrEmoji?: string | null }) =>
      api.post<Category>("/api/categories", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<{ name: string; colorHex: string | null; sfSymbolOrEmoji: string | null; sortOrder: number; archived: boolean }> }) =>
      api.patch<Category>(`/api/categories/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true; deleted: string }>(`/api/categories/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useCreateSubcategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { parentId: string; name: string }) => api.post<Subcategory>("/api/subcategories", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useUpdateSubcategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<{ name: string; sortOrder: number; archived: boolean; parentId: string }> }) =>
      api.patch<Subcategory>(`/api/subcategories/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useDeleteSubcategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true; deleted: string }>(`/api/subcategories/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

// ── Migration 0010: Sub-Subcategories (3. Hierarchie-Ebene) ──────────────
// Alle invalidieren ["categories"] (gleiche Query wie Cat/Sub).

export function useCreateSubSubcategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { parentId: string; name: string }) =>
      api.post<SubSubcategory>("/api/sub-subcategories", v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useUpdateSubSubcategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: {
      id: string;
      patch: Partial<{ name: string; sortOrder: number; archived: boolean; parentId: string }>;
    }) => api.patch<SubSubcategory>(`/api/sub-subcategories/${id}`, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useDeleteSubSubcategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ ok: true; deleted: string }>(`/api/sub-subcategories/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

// `useUpdateCategory` muss `stability` mitnehmen koennen — die alte Signatur ist offen genug
// (Partial), aber Stability ist ein neues Feld. Ein dedizierter Helper macht den Aufruf typsicher.
export function useSetCategoryStability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stability }: { id: string; stability: Stability | null }) =>
      api.patch<Category>(`/api/categories/${id}`, { stability }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

// Migration 0011: Neutral-Toggle für Cat (Saldenverschiebung). Cache invalidiert auch
// die Reports, weil die Σ-Buckets sich ändern.
export function useSetCategoryIsNeutral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isNeutral }: { id: string; isNeutral: boolean }) =>
      api.patch<Category>(`/api/categories/${id}`, { isNeutral }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["categories"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

// Migration 0012: defaultUsageType auf Top-Cat (vererbt sich an receipts.usageType-Default
// beim Beleg-Erfassen). Reine Cat-Konfig — keine Receipt-Invalidate nötig, nur categories-Cache.
export function useSetCategoryDefaultUsageType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, defaultUsageType }: { id: string; defaultUsageType: UsageType | null }) =>
      api.patch<Category>(`/api/categories/${id}`, { defaultUsageType }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

// Migration 0012: Stability + isNeutral auf Sub/SubSub (Leaf-Konstraint).
// Auch Reports invalidieren — Leaf-Lookup ändert die Σ-Buckets und die Stability-Filter.
export function useSetSubcategoryStability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stability }: { id: string; stability: Stability | null }) =>
      api.patch<Subcategory>(`/api/subcategories/${id}`, { stability }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["categories"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useSetSubcategoryIsNeutral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isNeutral }: { id: string; isNeutral: boolean }) =>
      api.patch<Subcategory>(`/api/subcategories/${id}`, { isNeutral }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["categories"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useSetSubSubcategoryStability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stability }: { id: string; stability: Stability | null }) =>
      api.patch<SubSubcategory>(`/api/sub-subcategories/${id}`, { stability }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["categories"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useSetSubSubcategoryIsNeutral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isNeutral }: { id: string; isNeutral: boolean }) =>
      api.patch<SubSubcategory>(`/api/sub-subcategories/${id}`, { isNeutral }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["categories"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

// ── Migration 0010: App-Settings (Achsen-Reihenfolge) ────────────────────

export type AppSettings = {
  ocrMode: "B" | "C";
  confidenceThreshold: number;
  vatMismatchToleranceEur: number;
  quantityMismatchToleranceEur: number;
  axisOrder: AxisType[];
  updatedAt: string | null;
};

export function useAppSettings() {
  return useQuery<AppSettings>({
    queryKey: ["app-settings"],
    queryFn: () => api.get("/api/app-settings"),
    staleTime: 60_000,
  });
}

export function useUpdateAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<AppSettings>) => api.patch<AppSettings>("/api/app-settings", patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["app-settings"] }),
  });
}

// ── Oberflächen-Paket: Buchungsart je Position ──────────────────────────────────────────────────────────────────
/** jahr = "standard": aktuelles Jahr, sonst das neueste mit Zuordnung (Einstellungen). */
export function useBuchungsarten(jahr: number | "standard" | null | undefined) {
  return useQuery<{ jahr: number | null; arten: Buchungsart[]; fehlt: boolean }>({
    queryKey: ["steuer-arten", jahr],
    queryFn: () => api.get(`/api/steuer-arten?jahr=${jahr}`),
    enabled: typeof jahr === "number" || jahr === "standard",
    staleTime: 5 * 60_000,
  });
}

function useBuchungsartMutation<V>(receiptId: string, fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["receipt", receiptId] });
      void qc.invalidateQueries({ queryKey: ["audit", receiptId] });
    },
  });
}

/** Art wählen (selbst gewählt) oder `{ vorschlag: true }` = zurück zum Vorschlag der Kategorie. */
export function useSetBuchungsart(receiptId: string) {
  return useBuchungsartMutation(receiptId, ({ itemId, steuerArtId }: { itemId: string; steuerArtId: string | "vorschlag" }) =>
    api.put(`/api/receipts/${receiptId}/line-items/${itemId}/buchungsart`,
      steuerArtId === "vorschlag" ? { vorschlag: true } : { steuerArtId }));
}

export function useSetAnteile(receiptId: string) {
  return useBuchungsartMutation(receiptId, ({ itemId, teile }: { itemId: string; teile: { steuerArtId: string; anteilBp: number }[] }) =>
    api.put(`/api/receipts/${receiptId}/line-items/${itemId}/anteile`, { teile }));
}

export function useDeleteAnteile(receiptId: string) {
  return useBuchungsartMutation(receiptId, (itemId: string) => api.del(`/api/receipts/${receiptId}/line-items/${itemId}/anteile`));
}

/** Buchungsart-Standard einer Ebene setzen (null = leeren). Wirkt nur für neue Positionen und beim Kategoriewechsel. */
export function useSetDefaultSteuerArt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ebene, id, steuerArtId }: { ebene: "categories" | "subcategories" | "sub-subcategories"; id: string; steuerArtId: string | null }) =>
      api.patch(`/api/${ebene}/${id}`, { defaultSteuerArtId: steuerArtId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}
